import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';
import * as orgService from '../services/organizationService';
import { capacityService } from '../services/capacityService';
import type {
  OrganizationWithRole,
  OrganizationMemberWithUser,
  OrganizationInvite,
  OrgMemberRole,
  ProjectMemberRole,
} from '../types';
import type { OrganizationContextValue } from '../types';

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

const ORG_STORAGE_KEY = 'meugantt_current_org';

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { hasFeature, limits } = useSubscription();

  const [organizations, setOrganizations] = useState<OrganizationWithRole[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ORG_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [orgMembers, setOrgMembers] = useState<OrganizationMemberWithUser[]>([]);
  const [orgInvites, setOrgInvites] = useState<OrganizationInvite[]>([]);
  const [loading, setLoading] = useState(true);

  // Org ativo derivado do state
  const currentOrg = useMemo(
    () => organizations.find(o => o.id === currentOrgId) || null,
    [organizations, currentOrgId]
  );

  // Carregar organizações do usuário
  const refreshOrganizations = useCallback(async () => {
    if (!user?.id) {
      setOrganizations([]);
      setLoading(false);
      return;
    }

    try {
      const orgs = await orgService.getUserOrganizations(user.id);
      setOrganizations(orgs);

      // Se a org salva não existe mais, resetar
      if (currentOrgId && !orgs.find(o => o.id === currentOrgId)) {
        setCurrentOrgId(null);
        localStorage.removeItem(ORG_STORAGE_KEY);
      }

      // Auto-selecionar a primeira org se nenhuma está selecionada
      if (!currentOrgId && orgs.length > 0) {
        setCurrentOrgId(orgs[0].id);
        localStorage.setItem(ORG_STORAGE_KEY, orgs[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar organizações:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, currentOrgId]);

  useEffect(() => {
    refreshOrganizations();
  }, [refreshOrganizations]);

  // Carregar membros e convites quando org muda
  const refreshMembers = useCallback(async () => {
    if (!currentOrgId) {
      setOrgMembers([]);
      setOrgInvites([]);
      return;
    }

    try {
      const [members, invites] = await Promise.all([
        orgService.getMembers(currentOrgId),
        orgService.getInvites(currentOrgId)
      ]);
      setOrgMembers(members);
      setOrgInvites(invites);
    } catch (err) {
      console.error('Erro ao carregar membros da org:', err);
    }
  }, [currentOrgId]);

  useEffect(() => {
    refreshMembers();
  }, [refreshMembers]);

  // Switch org
  const switchOrganization = useCallback((orgId: string | null) => {
    setCurrentOrgId(orgId);
    if (orgId) {
      localStorage.setItem(ORG_STORAGE_KEY, orgId);
    } else {
      localStorage.removeItem(ORG_STORAGE_KEY);
    }
  }, []);

  // Create org
  const createOrganization = useCallback(async (
    name: string,
    slug: string,
    description?: string
  ): Promise<OrganizationWithRole | null> => {
    if (!user?.id) return null;

    try {
      const org = await orgService.createOrganization(name, slug, description);
      const orgWithRole: OrganizationWithRole = {
        ...org,
        userRole: 'owner',
        memberCount: 1
      };
      setOrganizations(prev => [...prev, orgWithRole]);
      switchOrganization(org.id);
      return orgWithRole;
    } catch (err) {
      console.error('Erro ao criar organização:', err);
      throw err;
    }
  }, [user?.id, switchOrganization]);

  // Update org
  const updateOrganization = useCallback(async (
    id: string,
    updates: Partial<{ name: string; description: string; avatar_url: string }>
  ) => {
    const updated = await orgService.updateOrganization(id, updates);
    setOrganizations(prev =>
      prev.map(o => o.id === id ? { ...o, ...updated } : o)
    );
  }, []);

  // Delete org
  const deleteOrganization = useCallback(async (id: string) => {
    await orgService.deleteOrganization(id);
    setOrganizations(prev => prev.filter(o => o.id !== id));
    if (currentOrgId === id) {
      switchOrganization(null);
    }
  }, [currentOrgId, switchOrganization]);

  // Invite member
  const inviteMember = useCallback(async (email: string, role: OrgMemberRole) => {
    if (!currentOrgId || !user?.id) return { emailSent: false };
    const result = await orgService.inviteMember(currentOrgId, email, role, user.id);
    await refreshMembers();
    return { emailSent: result.emailSent };
  }, [currentOrgId, user?.id, refreshMembers]);

  // Remove member
  const removeMember = useCallback(async (userId: string) => {
    if (!currentOrgId) return;
    await orgService.removeMember(currentOrgId, userId);
    setOrgMembers(prev => prev.filter(m => m.user_id !== userId));
    // Atualizar contagem
    setOrganizations(prev =>
      prev.map(o => o.id === currentOrgId
        ? { ...o, memberCount: (o.memberCount || 1) - 1 }
        : o
      )
    );
  }, [currentOrgId]);

  // Update member role
  const updateMemberRole = useCallback(async (userId: string, newRole: OrgMemberRole) => {
    if (!currentOrgId) return;
    await orgService.updateMemberRole(currentOrgId, userId, newRole);
    setOrgMembers(prev =>
      prev.map(m => m.user_id === userId ? { ...m, role: newRole } : m)
    );
  }, [currentOrgId]);

  // Cancel invite
  const cancelInvite = useCallback(async (inviteId: string) => {
    await orgService.cancelInvite(inviteId);
    setOrgInvites(prev => prev.filter(i => i.id !== inviteId));
  }, []);

  // Move project to org
  const moveProjectToOrg = useCallback(async (projectId: string, orgId: string | null) => {
    await orgService.moveProjectToOrg(projectId, orgId);
  }, []);

  // Share project with all org members
  const shareProjectWithOrg = useCallback(async (
    projectId: string,
    defaultRole: ProjectMemberRole
  ): Promise<number> => {
    if (!currentOrgId || !user?.id) return 0;
    return await orgService.shareProjectWithOrgMembers(
      projectId,
      currentOrgId,
      defaultRole,
      user.id
    );
  }, [currentOrgId, user?.id]);

  // Sync org members as resources (manual button in ResourceManager)
  const syncResourcesFromMembers = useCallback(async (): Promise<number> => {
    if (!currentOrgId) return 0;
    try {
      const count = await capacityService.syncOrgMembersToResources(currentOrgId);
      return count;
    } catch (err) {
      console.error('Erro ao sincronizar recursos da org:', err);
      throw err;
    }
  }, [currentOrgId]);

  // Computed values
  const isOrgOwner = currentOrg?.userRole === 'owner';
  const isOrgAdmin = currentOrg?.userRole === 'owner' || currentOrg?.userRole === 'admin';
  const canCreateOrg = hasFeature('organizations');
  const orgMemberLimit = limits.org_members ?? 3;
  const orgMemberCount = currentOrg?.memberCount ?? 0;
  const canAddOrgMember = orgMemberLimit === -1 || orgMemberCount < orgMemberLimit;

  const value: OrganizationContextValue = useMemo(() => ({
    organizations,
    currentOrg,
    orgMembers,
    orgInvites,
    loading,
    createOrganization,
    switchOrganization,
    updateOrganization,
    deleteOrganization,
    inviteMember,
    removeMember,
    updateMemberRole,
    cancelInvite,
    moveProjectToOrg,
    shareProjectWithOrg,
    syncResourcesFromMembers,
    canCreateOrg,
    canAddOrgMember,
    orgMemberLimit,
    orgMemberCount,
    isOrgOwner,
    isOrgAdmin,
    refreshOrganizations,
    refreshMembers,
  }), [
    organizations, currentOrg, orgMembers, orgInvites, loading,
    createOrganization, switchOrganization, updateOrganization, deleteOrganization,
    inviteMember, removeMember, updateMemberRole, cancelInvite,
    moveProjectToOrg, shareProjectWithOrg, syncResourcesFromMembers,
    canCreateOrg, canAddOrgMember, orgMemberLimit, orgMemberCount,
    isOrgOwner, isOrgAdmin, refreshOrganizations, refreshMembers,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
}
